import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons';

import PINPEO, { STATUS_PILL } from 'theme/palette';

import Breadcrumb from './Breadcrumb';
import Divider from './Divider';

const useStyles = makeStyles((theme) => ({
  root: {},
  action: {
    marginRight: theme.spacing(1),
  },
  actionIcon: {
    marginRight: theme.spacing(1),
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: 13,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  button: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 13,
    borderRadius: PINPEO.radius,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  dangerButton: {
    color: theme.palette.common.white,
    backgroundColor: STATUS_PILL.error.fg,
    '&:hover': {
      backgroundColor: '#8F2C24',
    },
  },
  tabs: {
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 13.5,
      minWidth: 100,
    },
  },
}));

function Header(props) {
  const {
    actions,
    breadcrumb,
    breadcrumbPieces,
    tabs,
    title,
    subtitle,
  } = props;
  const classes = useStyles();
  const actionRef = useRef(null);
  const [openMenu, setOpenMenu] = useState({});
  const [menuValue, setMenuValue] = useState({});

  const handleClose = function (index) {
    setOpenMenu({
      ...openMenu,
      [`actions_menu_${index}`]: false,
    });
  };

  return (<>

    <Grid
      className={classes.root}
      container
      justifyContent="space-between"
      spacing={3}
    >
      <Grid item>
        {breadcrumb && <Breadcrumb
          pieces={breadcrumbPieces}
        />}
        <Typography className={classes.title} color="textPrimary">
          {title}
        </Typography>
        <Typography className={classes.subtitle}>
          {subtitle}
        </Typography>
      </Grid>
      {!!actions.length && <Grid item>
        {actions.map((item, index) => {
          let component;
          const {
            processing = false,
            disabled = processing || false,
          } = item;

          if (item.type === 'menu') {
            if (!menuValue[`actions_menu_${index}`]) {
              const selected = item.options.find(({ selected }) => selected);

              setMenuValue({
                ...menuValue,
                [`actions_menu_${index}`]: selected,
              });
            }

            component = (<>
              <Button
                children={menuValue[`actions_menu_${index}`]
                  ? menuValue[`actions_menu_${index}`].label
                  : ''}
                className={classes.button}
                color={item.color}
                endIcon={openMenu[`actions_menu_${index}`]
                  ? <ExpandLessIcon />
                  : <ExpandMoreIcon />}
                ref={actionRef}
                onClick={() => setOpenMenu({
                  ...openMenu,
                  [`actions_menu_${index}`]: true,
                })}
                startIcon={item.icon}
                variant="outlined"
              />
              <Menu
                anchorEl={actionRef.current}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                getContentAnchorEl={null}
                onClose={() => handleClose(index)}
                open={openMenu[`actions_menu_${index}`]}
                PaperProps={{
                  className: classes.menu,
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                {item.options.map((option, i) => (
                  <MenuItem
                    children={option.label}
                    key={i}
                    onClick={() => {
                      setMenuValue({
                        ...menuValue,
                        [`actions_menu_${index}`]: option,
                      });
                      handleClose(index);

                      if (typeof option.fn === 'function') {
                        option.fn(option);
                      }
                    }}
                  />
                ))}
              </Menu>
            </>);
          } else {
            component = (
              <Button
                children={item.label}
                className={clsx(
                  classes.button,
                  item.color === 'error' && !disabled
                    ? classes.dangerButton
                    : undefined,
                )}
                color={item.color === 'error'
                  ? 'default'
                  : (item.color || 'primary')}
                disabled={disabled || processing}
                key={index}
                form={item.form}
                onClick={item.fn}
                startIcon={processing ?
                  (<CircularProgress
                    color="inherit"
                    size={18}
                    style={{
                      marginRight: 5,
                    }}
                  />)
                  : item.icon}
                style={{
                  marginLeft: 5,
                  marginRight: 5,
                  display: (item.hidden ? 'none' : undefined),
                }}
                type={item.type}
                variant={item.variant || 'contained'}
              />
            );
          }

          return component;
        })}
      </Grid>}
    </Grid>

    {(tabs?.items || []).length > 0 && (<Box mt={2}>
      <Tabs
        className={classes.tabs}
        indicatorColor="primary"
        onChange={(_, value) => tabs.onChange(value)}
        scrollButtons="auto"
        textColor="primary"
        value={tabs.value}
        variant="scrollable"
      >
        {(tabs?.items || []).map((tab, index) => (
          <Tab
            key={index}
            label={tab.label}
            value={tab.value}
          />
        ))}
      </Tabs>
      <Divider />
    </Box>)}
  </>);
}

Header.defaultProps = {
  actions: [],
  breadcrumb: true,
  subtitle: '',
};

Header.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.oneOf(['error', 'default', 'primary', 'secondary']),
    disabled: PropTypes.bool,
    fn: PropTypes.func,
    form: PropTypes.string,
    icon: PropTypes.element,
    label: PropTypes.string,
    type: PropTypes.oneOf(['button', 'menu', 'submit']),
    variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  })),
  breadcrumb: PropTypes.bool,
  breadcrumbPieces: PropTypes.array,
  tabs: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
    })),
    onChange: PropTypes.func,
    value: PropTypes.string,
  }),
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

export default Header;
